// The tool set a chooser edits (FEATURE-20261101-mcp-url-tool-chooser), as godev pkg/rpc/mcp
// ToolSetAllows reads it: each entry is a tool name, a tool's entry, or a prefix ending in "*"
// matched against both. Empty offers every tool; whoami is always offered.
import type { McpToolGroup, McpToolInfo } from "../../services/mcp-url-client";

export const ALWAYS_OFFERED = "whoami";

/** Whether the set offers the tool. Mirrors ToolSetAllows. */
export function allows(set: string[], tool: McpToolInfo): boolean {
  if (set.length === 0 || tool.name === ALWAYS_OFFERED) return true;
  return set.some((e) => {
    if (e.endsWith("*")) {
      const p = e.slice(0, -1);
      return tool.name.startsWith(p) || tool.entry.startsWith(p);
    }
    return e === tool.name || e === tool.entry;
  });
}

/** Every tool under a group, depth first. */
export function leaves(g: McpToolGroup): McpToolInfo[] {
  return [...(g.tools ?? []), ...(g.groups ?? []).flatMap(leaves)];
}

/** Entries the tree does not account for (a custom prefix, a tool no longer served): kept as they are. */
export function otherEntries(set: string[], tree: McpToolGroup[]): string[] {
  const all = tree.flatMap(leaves);
  const groups: string[] = [];
  const walk = (g: McpToolGroup) => {
    if (g.entry) groups.push(g.entry);
    (g.groups ?? []).forEach(walk);
  };
  tree.forEach(walk);
  return set.filter((e) => !groups.includes(e) && !all.some((t) => allows([e], t) && t.name !== ALWAYS_OFFERED));
}

/**
 * The set for these checked tools, written compactly: a group whose every tool is checked is its
 * entry (so a tool added to it later is included); otherwise its parts. whoami is never written.
 * Entries the tree does not account for are kept. An empty result is written as ["whoami"], since
 * an empty set would mean every tool.
 */
export function compress(checked: Set<string>, tree: McpToolGroup[], keep: string[]): string[] {
  const out: string[] = [];
  const walk = (g: McpToolGroup) => {
    const ls = leaves(g).filter((t) => !t.alwaysOffered);
    if (ls.length === 0) return;
    if (g.entry && ls.every((t) => checked.has(t.entry))) {
      out.push(g.entry);
      return;
    }
    for (const t of g.tools ?? []) if (!t.alwaysOffered && checked.has(t.entry)) out.push(t.entry);
    (g.groups ?? []).forEach(walk);
  };
  tree.forEach(walk);
  const set = [...out, ...keep.filter((k) => !out.includes(k))];
  return set.length > 0 ? set : [ALWAYS_OFFERED];
}

/** The set after toggling these tools on or off. */
export function toggle(set: string[], tree: McpToolGroup[], tools: McpToolInfo[], on: boolean): string[] {
  const checked = new Set(tree.flatMap(leaves).filter((t) => allows(set, t)).map((t) => t.entry));
  for (const t of tools) {
    if (t.alwaysOffered) continue;
    if (on) checked.add(t.entry);
    else checked.delete(t.entry);
  }
  return compress(checked, tree, otherEntries(set, tree));
}
